import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { extractApiErrorMessage } from "../../../utils/apiError";
import { downloadFile } from "../../../utils/downloadUtils";

export interface ViewDocumentState {
  items: any[];
  loading: boolean;
  error: string | null;
  applicationDetails: any;
  applicationDetailsError: string | null;
  applicationDetailsLoading: boolean;
  managerInformation: any;
  managerInformationError: string | null;
  managerInformationLoading: boolean;
  managerSearchResult: any;
  managerSearchLoading: boolean;
  managerSearchError: string | null;
  managerUpdateError: string | null;
}

const initialState: ViewDocumentState = {
  items: [],
  loading: false,
  error: null,
  applicationDetails: null,
  applicationDetailsError: null,
  applicationDetailsLoading: false,
  managerInformation: null,
  managerInformationError: null,
  managerInformationLoading: false,
  managerSearchResult: null,
  managerSearchLoading: false,
  managerSearchError: null,
  managerUpdateError: null,
};

export const getApplicationList = createAsyncThunk(
  "viewDocument/getApplicationList",
  async (
    params: Record<string, any> | undefined,
    { rejectWithValue, signal }
  ) => {
    try {
      const options: any = { signal };
      if (params) options.params = params;
      const response = await api.get(
        endpoints.citizenship.viewDocuments.applicationsList,
        options
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch applications list"));
    }
  }
);

export const getDocumentsList = createAsyncThunk(
  "viewDocument/getDocumentsList",
  async (
    params: Record<string, any> | undefined,
    { rejectWithValue, signal }
  ) => {
    try {
      const options: any = { signal };
      if (params) options.params = params;
      const applicationId = params?.applicationId;
      const url = endpoints.citizenship.viewDocuments.documentsList(applicationId);
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch documents list"),
      );
    }
  }
);

export const uploadDocument = createAsyncThunk(
  "viewDocument/uploadDocument",
  async (
    {
      applicationId,
      file,
      description,
    }: {
      applicationId: string;
      file: File;
      description: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const documents = new FormData();
      documents.append("file", file);
      documents.append("description", description);

      await api.post(
        endpoints.citizenship.viewDocuments.uploadDocument(applicationId),
        documents,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return {
        applicationId,
        description,
        fileName: file.name,
      };
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to upload document"),
      );
    }
  }
);

export const fetchApplicationDetails = createAsyncThunk(
  "viewDocument/fetchApplicationDetails",
  async (
    applicationIdOrPayload:
      | string
      | { applicationId: string; empNuid?: string | null },
    { rejectWithValue, signal }
  ) => {
    try {
      const isString = typeof applicationIdOrPayload === "string";
      const applicationId = isString
        ? (applicationIdOrPayload)
        : (applicationIdOrPayload as any).applicationId;
      const empNuid = isString
        ? undefined
        : (applicationIdOrPayload as any).empNuid;

      if (!applicationId) throw new Error("applicationId is required");
      const url = endpoints.citizenship.viewDocuments.applicationDetails(applicationId);
      const options: any = { signal, headers: {} };
      if (empNuid) options.headers["X-emp-nuid"] = empNuid;
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch application details"));
    }
  }
);

export const fetchManagerInformation = createAsyncThunk(
  "viewDocument/fetchManagerInformation",
  async (
    applicationIdOrPayload:
      | string
      | { applicationId: string; empNuid?: string | null },
    { rejectWithValue, signal }
  ) => {
    try {
      const isString = typeof applicationIdOrPayload === "string";
      const applicationId = isString
        ? (applicationIdOrPayload)
        : (applicationIdOrPayload as any).applicationId;
      const empNuid = isString
        ? undefined
        : (applicationIdOrPayload as any).empNuid;

      if (!applicationId) throw new Error("applicationId is required");
      const url = endpoints.citizenship.viewDocuments.managerInformation(applicationId);
      const options: any = {
        signal,
        headers: {},
      };
      if (empNuid) {
        options.headers["X-emp-nuid"] = empNuid;
      }
      const response = await api.get(url, options);
      // Handle 204 No Content responses
      return response.data || {};
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }
      // Normalize error message using utility
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch manager information")
      );
    }
  }
);

export const downloadDocument = createAsyncThunk(
  "viewDocument/downloadDocument",
  async (
    {
      applicationId,
      documentId,
      fileName,
    }: { applicationId: string; documentId: string; fileName?: string },
    { rejectWithValue }
  ) => {
    try {
      const url = endpoints.citizenship.viewDocuments.downloadDocument(
        applicationId,
        documentId
      );
      const response = await api.get(url, { responseType: "blob" });
      
      downloadFile({
        response,
        documentId,
        fileName,
        defaultFileName: "document"
      });
 
      return { applicationId, documentId };
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to download document"),
      );
    }
  }
);
 

export const updateManagerDetails = createAsyncThunk(
  "viewDocument/updateManagerDetails",
  async (payload: { altMgrNuid: string }, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        endpoints.citizenship.viewDocuments.updateManagerDetails,
        payload
      );
      return response.data;
    } catch (error: any) {
      const message = error?.message || "Failed to update manager details";
      return rejectWithValue(extractApiErrorMessage(error, message));
    }
  }
);

export const getManagerByNUID = createAsyncThunk(
  "viewDocument/getManagerByNUID",
  async (nuid: string, { rejectWithValue, signal }) => {
    try {
      if (!nuid) throw new Error("NUID is required");

      const url = endpoints.citizenship.viewDocuments.searchManager;
      const options: any = {
        signal,
        headers: {
          "X-emp-nuid": nuid,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      };

      console.log("Calling API with NUID:", nuid);
      console.log("URL:", url);
      const response = await api.get(url, options);
      console.log("API Response:", response);
      // Handle 304 Not Modified by returning empty object
      if (response.status === 304 || !response.data) {
        console.log("Got 304 or empty response, returning empty object");
        return {};
      }
      console.log("Returning response data:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error in getManagerByNUID:", error);
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }

      // Extract error message from API response
      const errorMessage = extractApiErrorMessage(
        error,
        "Failed to fetch manager information",
      );

      console.log("Rejecting with error:", errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const viewDocumentSlice = createSlice({
  name: "viewDocument",
  initialState,
  reducers: {
    resetState: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApplicationList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getApplicationList.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      if (Array.isArray(payload)) {
        state.items = payload;
      } else if (payload?.items) {
        state.items = payload.items;
      } else if (payload?.data && Array.isArray(payload.data)) {
        state.items = payload.data;
      } else {
        state.items = payload ? [payload] : [];
      }
    });
    builder.addCase(getApplicationList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(getDocumentsList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getDocumentsList.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      if (Array.isArray(payload)) {
        state.items = payload;
      } else if (payload?.items) {
        state.items = payload.items;
      } else if (payload?.data && Array.isArray(payload.data)) {
        state.items = payload.data;
      } else {
        state.items = payload ? [payload] : [];
      }
    });
    builder.addCase(getDocumentsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(uploadDocument.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadDocument.fulfilled, (state, action) => {
      state.loading = false;
      console.log("Document uploaded successfully:", action.payload);
    });
    builder.addCase(uploadDocument.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // application details
    builder.addCase(fetchApplicationDetails.pending, (state) => {
      state.applicationDetailsError = null;
    });
    builder.addCase(fetchApplicationDetails.fulfilled, (state, action) => {
      state.applicationDetails = action.payload;
    });
    builder.addCase(fetchApplicationDetails.rejected, (state, action) => {
      state.applicationDetailsError = action.payload as string;
    });
    // manager information
    builder.addCase(fetchManagerInformation.pending, (state) => {
      state.managerInformationError = null;
    });
    builder.addCase(fetchManagerInformation.fulfilled, (state, action) => {
      state.managerInformation = action.payload;
    });
    builder.addCase(fetchManagerInformation.rejected, (state, action) => {
      state.managerInformationError = action.payload as string;
    });
    // download document
    builder.addCase(downloadDocument.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(downloadDocument.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(downloadDocument.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // update manager details
    builder.addCase(updateManagerDetails.pending, (state) => {
      state.loading = true;
      state.managerUpdateError = null;
    });
    builder.addCase(updateManagerDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.managerInformation = action.payload;
      state.managerUpdateError = null;
    });
    builder.addCase(updateManagerDetails.rejected, (state, action) => {
      state.loading = false;
      state.managerUpdateError = action.payload as string;
    });
    // get manager by NUID
    builder.addCase(getManagerByNUID.pending, (state) => {
      state.managerSearchLoading = true;
      state.managerSearchError = null;
      state.managerSearchResult = null;
    });
    builder.addCase(getManagerByNUID.fulfilled, (state, action) => {
      state.managerSearchLoading = false;
      state.managerSearchResult = action.payload;
    });
    builder.addCase(getManagerByNUID.rejected, (state, action) => {
      state.managerSearchLoading = false;
      state.managerSearchError = action.payload as string;
    });
  },
});

export const { resetState, setLoading, setError } = viewDocumentSlice.actions;
export default viewDocumentSlice.reducer;
